import { faArrowLeft, faMap } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { CSSProperties, KeyboardEvent, PointerEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ParcelRow } from "../projects/ParcelRow";
import { ProjectFiles } from "../projects/ProjectFiles";
import { ResearchDetail } from "../research/ResearchDetail";
import { ResearchPanel } from "../research/ResearchPanel";
import { StartResearchDialog } from "../research/StartResearchDialog";
import { useResearch } from "../research/store";
import { ParcelResearch } from "../research/types";
import { CaMapProvider, useAccount, useCaMap, useCaMapProvider, useProjects } from "../state";
import { ChatPanel } from "./chat/ChatPanel";
import { shouldLoadSession } from "./chat/sessionUrl";
import { useWorkspace } from "./chat/store";
import { ResearchProposal, pdfPending } from "./chat/types";
import { DetailPanel } from "./DetailPanel";
import { useProjectEvents } from "./events";
import { fitToParcels } from "./fit";
import { useWorkspaceKavel } from "./kavel";
import { Panel } from "./Panel";
import { useWorkspaceSearch } from "./search";
import {
  DEFAULT_SIDEBAR_WIDTH,
  MIN_SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_STORAGE_KEY,
  clampSidebarWidth,
  maxSidebarWidth,
} from "./sidebarWidth";
import { WorkspaceSearch } from "./WorkspaceSearch";

// Kerncijfers van het project; de kaart-kop rechts toont hetzelfde als er geen
// filter actief is.
const ProjectFacts = ({ project }: { project: any }) => {
  const map = useCaMap();
  const parcels = project.parcels || [];
  const area = parcels.reduce((sum: number, p: any) => (p.area || 0) + sum, 0);

  // Klikken zoomt de kaart terug naar het project — hetzelfde gebaar als de
  // projectnaam in ProjectBar op de globale kaart.
  return (
    <button
      onClick={() => fitToParcels(map, parcels)}
      disabled={!parcels.length}
      title={parcels.length ? "Zoom naar de projectpercelen" : undefined}
      className="w-full px-2 pb-2 text-left enabled:hover:bg-gray-50"
    >
      <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
        <dt className="text-gray-500">Percelen</dt>
        <dd className="text-right font-semibold">{parcels.length}</dd>
        <dt className="text-gray-500">Oppervlakte</dt>
        <dd className="text-right font-semibold">
          {area > 0 ? `${Math.round(area).toLocaleString("nl-NL")} m²` : "—"}
        </dd>
      </dl>
      {parcels.length > 0 && (
        <span className="text-[11px] text-gray-400">Klik om naar het project te zoomen</span>
      )}
    </button>
  );
};

const numeric = (value?: string) => {
  const n = value ? parseInt(value, 10) : NaN;
  return Number.isFinite(n) ? n : undefined;
};

export const Workspace = () => {
  const params = useParams();
  const navigate = useNavigate();
  const projectId = numeric(params.projectId);
  const sessionId = numeric(params.sessionId);
  const researchId = numeric(params.researchId);

  const user = useAccount((s) => s.user);
  const accountState = useAccount((s) => s.state);
  const projects = useProjects((s) => s.projects);
  const project = projects?.find((p) => p.id === projectId);

  const {
    activeSessionId,
    newSessionId,
    openProject,
    loadSessions,
    loadSession,
    clearNewSession,
    refreshReport,
    messages,
    isStreaming,
  } = useWorkspace();
  const {
    researches,
    projectLinks,
    detail: researchDetail,
    loading: researchLoading,
    error: researchError,
    completedNotice,
    openProject: openResearchProject,
    load: loadResearch,
    loadDetail: loadResearchDetail,
    start: startResearch,
    cancel: cancelResearch,
    retry: retryResearch,
    sourceAction,
    clearNotice,
  } = useResearch();

  // Bij een ander project (of een nieuw bezoek) niets van het vorige laten
  // staan: de zoek- en kavelstores leven buiten de component en onthouden
  // anders het filter en het aangeklikte kavel van daarvoor.
  useEffect(() => {
    if (!projectId) return;
    openProject(projectId);
    openResearchProject(projectId);
    void loadResearch();
    useWorkspaceSearch.getState().clear();
    useWorkspaceKavel.getState().clear();
  }, [projectId, openProject, openResearchProject, loadResearch]);

  // Zonder sessie in de URL het laatst gebruikte gesprek openen (de lijst staat
  // op -modified). Eén keer per project: daarna navigeert "nieuwe chat" naar de
  // URL zónder sessie, en die mag niet meteen terugspringen.
  const openedFor = useRef<number | undefined>(undefined);
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    if (!projectId) return;
    loadSessions().then((sessions) => {
      if (openedFor.current === projectId) return;
      openedFor.current = projectId;
      if (!sessionIdRef.current && !researchId && sessions.length) {
        navigate(`/workspace/${projectId}/${sessions[0].id}`, { replace: true });
      }
    });
  }, [projectId, researchId, loadSessions, navigate]);

  // De URL is leidend; de store volgt (zie shouldLoadSession). Bestaat de sessie
  // niet (of hoort hij bij een ander project), dan stil terug naar een lege chat.
  useEffect(() => {
    if (!shouldLoadSession({ urlSessionId: sessionId, activeSessionId, researchId, isStreaming }))
      return;
    let cancelled = false;
    loadSession(sessionId).then((found) => {
      if (!cancelled && sessionId && !found) {
        navigate(`/workspace/${projectId}`, { replace: true });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, researchId, activeSessionId, isStreaming, projectId, loadSession, navigate]);

  // Een sessie die de server tijdens het streamen aanmaakte, in de URL zetten.
  // Bewust op dat expliciete signaal en niet op "store en URL lopen uiteen":
  // vlak na een klik op "nieuwe chat" is de URL al leeg terwijl de store de
  // oude sessie nog heeft, en dan kaatste dit effect je meteen terug.
  useEffect(() => {
    if (!newSessionId) return;
    clearNewSession();
    navigate(`/workspace/${projectId}/${newSessionId}`, { replace: true });
  }, [newSessionId, clearNewSession, projectId, navigate]);

  const [showDetail, setShowDetail] = useState(false);
  const [researchParcel, setResearchParcel] = useState<any>();
  const [researchProposal, setResearchProposal] = useState<ResearchProposal>();
  const [researchPrevious, setResearchPrevious] = useState<ParcelResearch>();
  const closeResearchDialog = () => {
    setResearchParcel(undefined);
    setResearchProposal(undefined);
    setResearchPrevious(undefined);
  };
  const mapState = useCaMapProvider();
  const parcels = project?.parcels || [];
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = Number(window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY));
    return clampSidebarWidth(
      Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_SIDEBAR_WIDTH,
      window.innerWidth
    );
  });
  const resizingSidebar = useRef(false);

  const updateSidebarWidth = (width: number, persist = false) => {
    const nextWidth = clampSidebarWidth(width, window.innerWidth);
    setSidebarWidth(nextWidth);
    if (persist) window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(nextWidth));
  };

  const stopSidebarResize = (event: PointerEvent<HTMLDivElement>) => {
    if (!resizingSidebar.current) return;
    resizingSidebar.current = false;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
    updateSidebarWidth(event.clientX, true);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const resizeSidebarWithKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    updateSidebarWidth(sidebarWidth + (event.key === "ArrowLeft" ? -16 : 16), true);
  };

  useEffect(() => {
    const handleResize = () =>
      setSidebarWidth((width) => clampSidebarWidth(width, window.innerWidth));
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };
  }, []);

  // Perceel uit het vak: zelfde detailweergave als een kavel op de kaart.
  const showParcel = (parcel: any) => {
    const [lng, lat] = parcel.center.coordinates;
    useWorkspaceKavel.getState().select([lng, lat]);
    useWorkspaceKavel.getState().openReport();
    setShowDetail(true);
  };

  const onSelectSession = (id?: number) =>
    navigate(id ? `/workspace/${projectId}/${id}` : `/workspace/${projectId}`);

  useEffect(() => {
    if (!researchId) return;
    void loadResearchDetail(researchId);
    setShowDetail(true);
  }, [researchId, loadResearchDetail]);

  const importingDocument = researchDetail?.sources.some((source) =>
    ["queued", "importing"].includes(source.documentState)
  );
  const detailId = researchId || (importingDocument ? researchDetail?.id : undefined);

  const reloadResearch = useCallback(() => {
    void loadResearch().then(() => {
      if (detailId) void loadResearchDetail(detailId);
    });
  }, [loadResearch, loadResearchDetail, detailId]);

  // De server meldt zelf wanneer er iets veranderd is; het seintje zegt alleen
  // wát, dus we halen de verse stand langs de gewone weg op.
  const connection = useProjectEvents(projectId, (signal) => {
    if (signal.kind === "report") {
      void refreshReport(signal.id);
      return;
    }
    // Ook na een onderbreking: wat we toen misten, staat in de lijsten.
    reloadResearch();
  });

  // Rapporten waarvan de PDF nog gemaakt wordt; die horen ook bij het vangnet.
  const pendingReports = messages
    .flatMap((message) => message.reportProposals ?? [])
    .filter((proposal) => pdfPending(proposal.pdfStatus))
    .map((proposal) => proposal.id);
  const pendingReportKey = pendingReports.join(",");

  // Vangnet voor het geval een seintje niet aankomt (verbinding weg, server
  // herstart): veel rustiger dan de poll van vroeger, en alleen zolang er
  // echt iets loopt. Dit is de enige timer die nog rondgaat.
  useEffect(() => {
    const active = researches.some((item) => item.status === "queued" || item.status === "running");
    if (!active && !importingDocument && !pendingReportKey) return;
    const timer = window.setInterval(() => {
      if (active || importingDocument) reloadResearch();
      for (const id of pendingReportKey ? pendingReportKey.split(",") : []) {
        void refreshReport(Number(id));
      }
    }, 60000);
    return () => window.clearInterval(timer);
  }, [researches, importingDocument, pendingReportKey, reloadResearch, refreshReport]);

  const selectResearch = (id: number) => navigate(`/workspace/${projectId}/research/${id}`);
  const importedFileToken = (researchDetail?.sources || [])
    .flatMap((source) => (source.projectFileUrl ? [source.projectFileUrl] : []))
    .sort()
    .join("|");

  // Bij een directe URL is het account nog niet opgehaald; pas oordelen als het
  // binnen is, anders vliegt ook een staff-gebruiker er meteen uit.
  if (accountState !== "fetched" || !projects) return null;
  if (!user?.features?.workspace || !projectId) return <Navigate to="/projects" replace />;
  if (!project) return <Navigate to="/projects" replace />;

  return (
    <WorkspaceSearch>
      {/* De kaart-context omvat beide kolommen, zodat ook het project-vak links
          de workspace-kaart kan aansturen. */}
      <CaMapProvider value={mapState}>
        <main className="fixed inset-0 top-14 flex bg-white">
          <div
            className="flex w-full flex-col border-r sm:w-[var(--workspace-sidebar-width)] sm:shrink-0"
            style={{ "--workspace-sidebar-width": `${sidebarWidth}px` } as CSSProperties}
          >
            <div className="flex items-center gap-2 border-b px-2 py-2">
              <Link
                to={`/projects/${projectId}`}
                className="text-gray-500 hover:text-black"
                title="Terug naar het project"
              >
                <FontAwesomeIcon icon={faArrowLeft} size="sm" />
              </Link>
              <span className="flex-1 truncate text-sm font-bold">{project.name}</span>
              <button
                onClick={() => setShowDetail(true)}
                className="text-gray-500 hover:text-black sm:hidden"
                title="Toon de kaart"
                aria-label="Toon de kaart"
              >
                <FontAwesomeIcon icon={faMap} size="sm" />
              </button>
            </div>
            {connection === "broken" && (
              // De browser verbindt zelf opnieuw; tot die tijd lopen nieuwe
              // statussen tot een minuut achter (het vangnet hieronder).
              <p className="bg-yellow/20 px-3 py-1 text-xs text-gray-600">
                Live-updates zijn even weg; de stand ververst wat trager.
              </p>
            )}
            {/* Eigen scrollgebied: de vakken samen kunnen hoger zijn dan de
                zijbalk, en dan moet de kop staan blijven. */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
              <Panel id="project" title="Project">
                <ProjectFacts project={project} />
              </Panel>
              <Panel id="chat" title="Chat" grow>
                <ChatPanel
                  parcels={parcels}
                  onSelectSession={onSelectSession}
                  onApplyFilter={() => setShowDetail(true)}
                  onResearchProposal={(proposal) => {
                    const parcel = parcels.find((item: any) => item.id === proposal.parcelId);
                    if (!parcel) return;
                    setResearchProposal(proposal);
                    setResearchParcel(parcel);
                  }}
                />
              </Panel>
              <Panel id="research" title="Onderzoeken" count={researches.length}>
                <ResearchPanel
                  researches={researches}
                  projectLinks={projectLinks}
                  onSelect={selectResearch}
                />
                {researchError && <p className="px-2 pb-2 text-xs text-red-600">{researchError}</p>}
              </Panel>
              <Panel id="parcels" title="Percelen" count={parcels.length}>
                {parcels.length === 0 ? (
                  <p className="px-2 pb-2 text-xs text-gray-400">
                    Nog geen percelen — zoek kavels via de chat
                  </p>
                ) : (
                  <ul className="pb-1">
                    {parcels.map((parcel: any) => (
                      <ParcelRow
                        key={parcel.id}
                        parcel={parcel}
                        projectId={project.id}
                        onSelect={showParcel}
                        onResearch={setResearchParcel}
                      />
                    ))}
                  </ul>
                )}
              </Panel>
              <Panel id="files" title="Bestanden">
                <ProjectFiles projectId={project.id} refreshToken={importedFileToken} />
              </Panel>
            </div>
          </div>

          <div
            role="separator"
            aria-label="Breedte van de zijbalk aanpassen"
            aria-orientation="vertical"
            aria-valuemin={MIN_SIDEBAR_WIDTH}
            aria-valuemax={maxSidebarWidth(window.innerWidth)}
            aria-valuenow={sidebarWidth}
            tabIndex={0}
            className="group hidden w-2 shrink-0 touch-none cursor-col-resize items-center justify-center focus:outline-none focus-visible:bg-gray-100 sm:flex"
            onPointerDown={(event) => {
              resizingSidebar.current = true;
              event.currentTarget.setPointerCapture(event.pointerId);
              document.body.style.cursor = "col-resize";
              document.body.style.userSelect = "none";
            }}
            onPointerMove={(event) => {
              if (resizingSidebar.current) updateSidebarWidth(event.clientX);
            }}
            onPointerUp={stopSidebarResize}
            onPointerCancel={stopSidebarResize}
            onKeyDown={resizeSidebarWithKeyboard}
          >
            <span className="h-full w-px bg-gray-200 group-hover:bg-gray-500 group-focus-visible:bg-gray-500" />
          </div>

          {/* Desktop: vaste tweede kolom. Smal scherm: overlay met terugknop.
            Eén DetailPanel, want elke instantie is een eigen MapLibre-kaart. */}
          <div
            className={`flex-col sm:flex sm:flex-1 ${
              showDetail ? "absolute inset-0 z-20 flex bg-white sm:static sm:z-auto" : "hidden"
            }`}
          >
            {showDetail && (
              <button
                onClick={() => setShowDetail(false)}
                className="flex items-center gap-2 border-b px-3 py-2 text-sm text-gray-600 sm:hidden"
              >
                <FontAwesomeIcon icon={faArrowLeft} size="sm" /> Terug naar de chat
              </button>
            )}
            <div className="min-h-0 flex-1">
              {researchId && researchDetail?.id === researchId ? (
                <ResearchDetail
                  research={researchDetail}
                  onCancel={() => void cancelResearch(researchId)}
                  onRetry={() =>
                    void retryResearch(researchId).then((id) => id && selectResearch(id))
                  }
                  onRerun={() => {
                    const parcelId = researchDetail.targets[0]?.parcelId;
                    const parcel = parcels.find((item: any) => item.id === parcelId);
                    if (!parcel) {
                      useResearch.setState({ error: "De oorspronkelijke kavel bestaat niet meer" });
                      return;
                    }
                    setResearchPrevious(researchDetail);
                    setResearchParcel(parcel);
                  }}
                  onSourceAction={(sourceId, action) =>
                    void sourceAction(researchId, sourceId, action)
                  }
                />
              ) : researchId && researchLoading ? (
                <p className="p-4 text-sm text-gray-500">Onderzoek laden…</p>
              ) : (
                <DetailPanel key={project.id} project={project} />
              )}
            </div>
          </div>
          {researchParcel && (
            <StartResearchDialog
              parcel={researchParcel}
              busy={researchLoading}
              title={researchPrevious ? "Opnieuw onderzoeken" : undefined}
              initial={
                researchPrevious?.setup ?? { question: researchProposal?.extraQuestion ?? "" }
              }
              onClose={closeResearchDialog}
              onStart={(choice) =>
                void startResearch(researchParcel.id, choice, {
                  // Een nieuwe versie hoort bij de chat van zijn voorganger;
                  // dat regelt de server.
                  chatSessionId: researchPrevious ? undefined : activeSessionId,
                  proposalId: researchProposal?.id,
                  previousVersionId: researchPrevious?.id,
                }).then((id) => {
                  if (!id) return;
                  closeResearchDialog();
                  selectResearch(id);
                })
              }
            />
          )}
          {completedNotice && (
            <button
              type="button"
              onClick={() => {
                selectResearch(completedNotice.id);
                clearNotice();
              }}
              className="fixed bottom-4 right-4 z-40 rounded bg-dark-green px-4 py-3 text-sm text-white shadow-lg"
            >
              Kavelonderzoek gereed — bekijk resultaat
            </button>
          )}
        </main>
      </CaMapProvider>
    </WorkspaceSearch>
  );
};
